// src/components/Pages/SmartForecast/DetailedAnalysisSection.jsx
import React from "react";
import {
  Filter,
  AlertCircle,
  Loader2,
  Activity,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { formatCurrency, formatPercent, formatVND, formatVND1000 } from '../../../../utils/format';
// Mang hàm vẽ biểu đồ từ file chính sang đây vì chỉ component này dùng
const renderDetailedTrendLine = (trendsData) => {
  if (!trendsData || trendsData.length === 0) return null;
  const data = trendsData.map((d) => parseFloat(d.gmw));
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const color = "#3b82f6"; // Blue for detailed trends
  const points = data
    .map((val, idx) => {
      const x = (idx / (data.length - 1)) * 100;
      const y = 100 - ((val - min) / range) * 80 - 10;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 100"
      className="w-full h-full overflow-visible"
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id="gradient-detailed" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill="url(#gradient-detailed)"
      />
    </svg>
  );
};

export default function DetailedAnalysisSection({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  growthData,
  isGrowthLoading,
  growthError,
}) {
  const GrowthBadge = ({ value, label }) => {
    const isPos = value >= 0;
    return (
      <div className={`flex flex-col items-end`}>
        <span
          className={`text-[12px] font-bold flex items-center gap-1 px-2.5 py-0.5 rounded-md ${isPos ? "text-emerald-700 bg-emerald-50 border border-emerald-100" : "text-red-700 bg-red-50 border border-red-100"}`}
        >
          {isPos ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{" "}
          {formatPercent(value || 0)}
        </span>
        {label && (
          <span className="text-[10px] uppercase font-bold text-slate-400 mt-1">
            {label}
          </span>
        )}
      </div>
    );
  };

  const formatChartLabel = (periodStr, granularity) => {
    if (!periodStr) return "";
    if (granularity === "hour") {
      const parts = periodStr.split(" ");
      return parts.length > 1 ? `${parts[1]}h` : periodStr;
    } else {
      const parts = periodStr.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
      return periodStr;
    }
  };

  const formatTablePeriod = (periodStr, granularity) => {
    if (!periodStr) return "";
    if (granularity === "hour") {
      const [date, hour] = periodStr.split(" ");
      if (date) {
        const parts = date.split("-");
        if (parts.length === 3) {
          const h = hour ? hour.padStart(2, '0') : "00";
          return `${parts[2]}/${parts[1]}/${parts[0]} ${h}:00`;
        }
      }
      return periodStr;
    } else {
      const parts = periodStr.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return periodStr;
    }
  };
  return (
    <div className="space-y-6 mt-8 p-6 bg-slate-100 rounded-2xl border border-slate-200">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Filter size={24} className="text-blue-600" /> Phân Tích Chuyên Sâu
          Giai Đoạn
        </h2>

        {/* Form tìm kiếm độc lập cho phần Detailed Analytics */}
        <form className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
            <span className="text-xs font-semibold text-slate-500">Từ</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-sm outline-none bg-transparent font-medium text-slate-700"
            />
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
            <span className="text-xs font-semibold text-slate-500">Đến</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="text-sm outline-none bg-transparent font-medium text-slate-700"
            />
          </div>
        </form>
      </div>

      {growthError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 border border-red-200 shadow-sm">
          <AlertCircle size={20} />
          <span>{growthError}</span>
        </div>
      )}

      {isGrowthLoading && !growthError && (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-blue-500" size={32} />
        </div>
      )}

      {!isGrowthLoading && growthData && !growthError && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Metrics & Growth */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-5 flex items-center gap-2">
                <Activity size={16} className="text-blue-600" /> So Sánh Kỳ
              </h4>
              <div className="space-y-4">
                {/* GMW */}
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-sm font-bold text-slate-600 mb-1">
                      GMW (Tổng GD)
                    </p>
                    <div className="flex flex-col">
                      <span className="text-xl font-bold text-slate-800">
                        {formatCurrency(growthData.current_period.gmw)}
                      </span>
                      <span className="text-xs text-slate-400">
                        Kỳ trước:{" "}
                        {growthData.previous_period ? formatCurrency(growthData.previous_period.gmw) : "Không có GD"}
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const prevGmw = growthData.previous_period?.gmw || 0;
                    if (!growthData.previous_period || prevGmw === 0) return <span className="text-xs text-slate-300 italic">-</span>;
                    const diff = growthData.current_period.gmw - prevGmw;
                    const percent = (diff / prevGmw) * 100;
                    return (
                      <div
                        className={`text-xs font-bold px-2 py-1 rounded ${diff >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {diff > 0 ? "+" : ""}
                        {percent.toFixed(1)}%
                      </div>
                    );
                  })()}
                </div>

                {/* Số lượng GD */}
                <div className="flex justify-between items-end pt-3 border-t border-slate-50">
                  <div>
                    <p className="text-sm font-bold text-slate-600 mb-1">
                      Số lượng GD
                    </p>
                    <div className="flex flex-col">
                      <span className="text-lg font-semibold text-slate-700">
                        {growthData.current_period.txn_count}
                      </span>
                      <span className="text-xs text-slate-400">
                        Kỳ trước: {growthData.previous_period ? growthData.previous_period.txn_count : "Không có GD"}
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const prevTxn = growthData.previous_period?.txn_count || 0;
                    if (!growthData.previous_period || prevTxn === 0) return <span className="text-xs text-slate-300 italic">-</span>;
                    const diff = growthData.current_period.txn_count - prevTxn;
                    const percent = (diff / prevTxn) * 100;
                    return (
                      <div
                        className={`text-xs font-bold px-2 py-1 rounded ${diff >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {diff > 0 ? "+" : ""}
                        {percent.toFixed(1)}%
                      </div>
                    );
                  })()}
                </div>

                {/* AOV */}
                <div className="flex justify-between items-end pt-3 border-t border-slate-50">
                  <div>
                    <p className="text-sm font-bold text-slate-600 mb-1">
                      AOV (TB/Đơn)
                    </p>
                    <div className="flex flex-col">
                      <span className="text-lg font-semibold text-slate-700">
                        {formatVND1000(growthData.current_period.aov)}
                      </span>
                      <span className="text-xs text-slate-400">
                        Kỳ trước:{" "}
                        {growthData.previous_period ? formatCurrency(growthData.previous_period.aov) : "Không có GD"}
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const prevAov = growthData.previous_period?.aov || 0;
                    if (!growthData.previous_period || prevAov === 0) return <span className="text-xs text-slate-300 italic">-</span>;
                    const diff = growthData.current_period.aov - prevAov;
                    const percent = (diff / prevAov) * 100;
                    return (
                      <div
                        className={`text-xs font-bold px-2 py-1 rounded ${diff >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {diff > 0 ? "+" : ""}
                        {percent.toFixed(1)}%
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                Tốc Độ Tăng Trưởng
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-bold text-slate-600">
                    DoD{" "}
                    <span className="text-xs font-medium text-slate-400 font-normal ml-1">
                      (Ngày)
                    </span>
                  </p>
                  <GrowthBadge value={growthData.growth_rates.dod} />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-bold text-slate-600">
                    WoW{" "}
                    <span className="text-xs font-medium text-slate-400 font-normal ml-1">
                      (Tuần)
                    </span>
                  </p>
                  <GrowthBadge value={growthData.growth_rates.wow} />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-bold text-slate-600">
                    MoM{" "}
                    <span className="text-xs font-medium text-slate-400 font-normal ml-1">
                      (Tháng)
                    </span>
                  </p>
                  <GrowthBadge value={growthData.growth_rates.mom} />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Time Trends Line Chart */}
          <div className="lg:col-span-3 bg-white rounded-xl p-6 shadow-sm border border-slate-200 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-base font-bold text-slate-800">
                  Xu Hướng GMW Theo{" "}
                  {growthData.time_trends.granularity === "hour"
                    ? "Giờ"
                    : "Ngày"}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Từ {growthData.date_range.start_date} đến{" "}
                  {growthData.date_range.end_date}
                </p>
              </div>
            </div>
            <div className="flex-1 w-full min-h-[150px] relative bg-slate-50/50 rounded-lg p-4 border border-slate-100">
              {growthData.time_trends &&
              growthData.time_trends.trends.length > 0 ? (
                <div className="h-24 w-full pt-4 relative group">
                  {renderDetailedTrendLine(growthData.time_trends.trends)}
                  {/* Custom X-Axis labels for line chart */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-slate-400 px-1 mt-2 transform translate-y-full">
                    <span>
                      {formatChartLabel(growthData.time_trends.trends[0].period, growthData.time_trends.granularity)}
                    </span>
                    <span>
                      {formatChartLabel(
                        growthData.time_trends.trends[Math.floor(growthData.time_trends.trends.length / 2)].period,
                        growthData.time_trends.granularity
                      )}
                    </span>
                    <span>
                      {formatChartLabel(
                        growthData.time_trends.trends[growthData.time_trends.trends.length - 1].period,
                        growthData.time_trends.granularity
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                  Không đủ dữ liệu xu hướng
                </div>
              )}
            </div>

            {/* Optional table view of trends for clarity */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
              <div className="max-h-[250px] overflow-x-auto overflow-y-auto custom-scrollbar">
                <table className="w-full text-left text-sm min-w-[600px]">
                  <thead className="sticky top-0 bg-slate-100 shadow-sm z-10 border-b border-slate-200">
                    <tr className="text-slate-600">
                      <th className="py-4 px-5 font-black uppercase tracking-widest text-xs">
                        Thời gian
                      </th>
                      <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                        Tổng GMW
                      </th>
                      <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                        Số GD
                      </th>
                      <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                        AOV (TB/Đơn)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {growthData.time_trends?.trends.map((t, i) => (
                      <tr
                        key={i}
                        className="even:bg-slate-50 hover:bg-blue-50 transition-colors"
                      >
                        <td className="py-4 px-5 font-bold text-slate-700 whitespace-nowrap">
                          {formatTablePeriod(t.period, growthData.time_trends.granularity)}
                        </td>
                        <td className="py-4 px-5 text-right font-black text-slate-900 tabular-nums">
                          {formatCurrency(t.gmw)}
                        </td>
                        <td className="py-4 px-5 text-right font-bold text-slate-600 tabular-nums">
                          {t.txn_count}
                        </td>
                        <td className="py-4 px-5 text-right font-bold text-slate-500 tabular-nums">
                          {formatVND1000(t.aov)}
                        </td>
                      </tr>
                    ))}
                    {/* Empty state cho Table */}
                    {(!growthData.time_trends ||
                      growthData.time_trends.trends.length === 0) && (
                      <tr>
                        <td
                          colSpan="4"
                          className="py-8 text-center text-slate-500 font-medium"
                        >
                          Chưa có dữ liệu giao dịch trong khoảng thời gian này.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
